import {
  Action,
  Badge,
  Calendar,
  Card,
  FieldGroup,
  Icon,
  Label,
  NumberInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SimpleSelect,
  SupportingText,
  MultiSelect,
  MultiSelectOption,
} from 'corso-admin-ui';
import { CrewMerchantUi, Pegasus, isTruthy } from 'corso-types';
import { useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useProtectionRates } from '~/features/settings/hooks/useProtectionRates';
import { ShippingRate } from '~/features/settings/shippingPlus/ShippingRate';
import { useStoreUser } from '~/providers/StoreUserProvider';
import {
  estimatedDeliveryShippingCutOffOptions,
  usHolidayOptions,
} from '~/utils/enumNameMaps';
import { formatter } from 'corso-web-lib';

function useStoreTimezone() {
  const {
    store: { ianaTimezone },
  } = useStoreUser();

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return ianaTimezone ?? userTimeZone;
}

const estimatedDeliveryStrategyText = {
  CARRIER_PROVIDED: 'Carrier Provided',
  STATIC: 'Predefined Estimate',
} as const satisfies Record<Pegasus.EstimatedDeliveryStrategy, string>;

const estimatedDeliveryStrategyOptions = Object.values(
  Pegasus.EstimatedDeliveryStrategy,
).map((strategy) => ({
  value: strategy,
  label: estimatedDeliveryStrategyText[strategy],
}));

function BlackoutDatesPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (dates: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const addDate = (date: Date | undefined) => {
    if (date) {
      const isoDate = date.toISOString();
      if (!value.includes(isoDate)) {
        onChange([...value, isoDate]);
      }
      setIsOpen(false);
    }
  };

  const removeDate = (dateToRemove: string) => {
    onChange(value.filter((d) => d !== dateToRemove));
  };

  const sortedDates = value.sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  return (
    <FieldGroup>
      <Label htmlFor="add-blackout-date">Blackout Dates</Label>
      <SupportingText>
        Custom dates to exclude when calculating the estimated delivery date,
        indicating dates a warehouse will not ship on.
      </SupportingText>

      {sortedDates.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {sortedDates.map((date) => (
            <button
              key={date}
              type="button"
              onClick={() => removeDate(date)}
              aria-label={`Remove ${formatter.date(date)}`}
            >
              <Badge hoverable className="gap-1">
                {formatter.date(date)}
                <Icon icon="close" className="h-3.5 w-3.5" />
              </Badge>
            </button>
          ))}
        </div>
      )}

      <div className="mt-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Action icon="add" id="add-blackout-date">
              Add Blackout Date
            </Action>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={undefined}
              onSelect={addDate}
              disabled={[
                { before: new Date() },
                ...sortedDates.map((date) => new Date(date)),
              ]}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </FieldGroup>
  );
}

const getEstimateRateOptions = (
  protectionRates: CrewMerchantUi.ProtectionRate[],
  carrierOnly: boolean,
) => {
  const allProtectionRateOptions = protectionRates.map(
    (rate) =>
      ({
        value: `${rate.id}`,
        keywords: [rate.name, rate.description].filter(isTruthy),
        label: <ShippingRate rate={rate} />,
      }) satisfies MultiSelectOption,
  );

  const carrierProtectionRateOptions = protectionRates
    .filter((r) => r.carrierService)
    .map(
      (rate) =>
        ({
          value: `${rate.id}`,
          keywords: [rate.name, rate.description].filter(isTruthy),
          label: <ShippingRate rate={rate} />,
        }) satisfies MultiSelectOption,
    );

  return carrierOnly ? carrierProtectionRateOptions : allProtectionRateOptions;
};

export default function EventQuoteOrderShipmentDeliveryEstimate() {
  const { control, watch } = useAutomationFormContext();
  const { data: protectionRates } = useProtectionRates();
  const timezone = useStoreTimezone(); // Ensuring store timezone is used

  const {
    fields: deliveryEstimateConfigs,

    append,
    remove,
  } = useFieldArray({
    control,
    name: 'event.params.deliveryEstimateConfigs',
  });

  return (
    <div className="flex flex-col gap-2">
      {deliveryEstimateConfigs.map((field, index) => {
        const strategy = watch(
          `event.params.deliveryEstimateConfigs.${index}.strategy`,
        );

        const protectionRateOptions = getEstimateRateOptions(
          protectionRates,
          strategy === 'CARRIER_PROVIDED',
        );

        return (
          <Card key={field.id}>
            <Card.Heading>Estimated Delivery Configuration</Card.Heading>

            <Controller
              control={control}
              name={`event.params.deliveryEstimateConfigs.${index}.strategy`}
              render={({ field: f, fieldState }) => (
                <SimpleSelect
                  label="Strategy"
                  options={estimatedDeliveryStrategyOptions}
                  onChange={f.onChange}
                  value={f.value}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name={`event.params.deliveryEstimateConfigs.${index}.estimatedDeliveryDisplayLocation`}
              render={({ field: f, fieldState }) => (
                <SimpleSelect
                  label="Display Location"
                  options={[
                    {
                      value: 'ADD_TO_DESCRIPTION',
                      label: 'Add To Description',
                    },
                    {
                      value: 'EXTENSION',
                      label: 'Delivery Estimate Extension',
                    },
                  ]}
                  onChange={f.onChange}
                  value={f.value}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name={`event.params.deliveryEstimateConfigs.${index}.estimatedTargetStoreShipRateIds`}
              render={({ field: f, fieldState }) => (
                <MultiSelect
                  togglable
                  label="Shipping Rates"
                  options={protectionRateOptions}
                  details="The rates to apply the delivery estimate to, if left empty all rates will be updated."
                  placeholder="Select Rates"
                  value={f.value
                    .map((value) =>
                      protectionRateOptions.find(
                        (option) => option.value === `${value}`,
                      ),
                    )
                    .filter(isTruthy)}
                  onChange={(selected) =>
                    f.onChange(selected.map(({ value }) => Number(value)))
                  }
                  error={fieldState.error?.message}
                />
              )}
            />

            {strategy === 'CARRIER_PROVIDED' && (
              <Controller
                control={control}
                name={`event.params.deliveryEstimateConfigs.${index}.carrierEstimatedFulfillmentDays`}
                render={({ field: f, fieldState }) => (
                  <NumberInput
                    id={`estimated-delivery-range-start-${index}`}
                    label="Fulfillment Days"
                    details="The number of additional days to add to the carrier provided estimated delivery date, to account for fulfillment."
                    addon={{ insideEnd: 'days' }}
                    value={f.value}
                    error={fieldState.error?.message}
                    onChange={(e) => f.onChange(e.target.valueAsNumber)}
                  />
                )}
              />
            )}

            {strategy === 'STATIC' && (
              <>
                <Controller
                  control={control}
                  name={`event.params.deliveryEstimateConfigs.${index}.estimatedDeliveryRangeStartDays`}
                  render={({ field: f, fieldState }) => (
                    <NumberInput
                      id={`estimated-delivery-range-start-${index}`}
                      label="Estimated Delivery Start"
                      details="The number of business days to the earliest possible delivery date."
                      addon={{ insideEnd: 'days' }}
                      value={f.value}
                      error={fieldState.error?.message}
                      onChange={(e) => f.onChange(e.target.valueAsNumber)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`event.params.deliveryEstimateConfigs.${index}.estimatedDeliveryRangeEndDays`}
                  render={({ field: f, fieldState }) => (
                    <NumberInput
                      id={`estimated-delivery-range-end-${index}`}
                      label="Estimated Delivery End"
                      details="The number of business days to the latest possible delivery date."
                      value={f.value}
                      addon={{ insideEnd: 'days' }}
                      error={fieldState.error?.message}
                      onChange={(e) => f.onChange(e.target.valueAsNumber)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`event.params.deliveryEstimateConfigs.${index}.estimatedDeliveryShippingCutOffTime`}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <SimpleSelect
                      options={estimatedDeliveryShippingCutOffOptions.map(
                        (option) => ({
                          value: `${option.value}`,
                          label: option.label,
                        }),
                      )}
                      details="The time of day to cut off shipping for the estimated delivery date."
                      label="Shipping Cut Off Time"
                      onChange={onChange}
                      value={`${value}`}
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`event.params.deliveryEstimateConfigs.${index}.estimatedDeliveryHolidays`}
                  render={({ field: f, fieldState }) => (
                    <MultiSelect
                      label="Holidays"
                      options={usHolidayOptions}
                      details="The US Holidays to use when calculating the estimated delivery date."
                      placeholder="Select Holidays"
                      value={f.value
                        ?.map((value) =>
                          usHolidayOptions.find(
                            (option) => option.value === `${value}`,
                          ),
                        )
                        .filter(isTruthy)}
                      onChange={(selected) =>
                        f.onChange(selected.map(({ value }) => value))
                      }
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`event.params.deliveryEstimateConfigs.${index}.estimatedDeliveryBlackoutDates`}
                  render={({ field: f }) => (
                    <BlackoutDatesPicker
                      value={f.value ?? []}
                      onChange={f.onChange}
                    />
                  )}
                />
              </>
            )}

            <div className="flex">
              <Action onClick={() => remove(index)} variant="destructive">
                Remove
              </Action>
            </div>
          </Card>
        );
      })}

      <div>
        <Action
          icon="add"
          onClick={() =>
            append({
              strategy: 'STATIC',
              estimatedDeliveryDisplayLocation: 'ADD_TO_DESCRIPTION',
              estimatedDeliveryRangeStartDays: 3,
              estimatedDeliveryRangeEndDays: 7,
              estimatedTargetStoreShipRateIds: [],
              estimatedDeliveryShippingCutOffTime: 0,
              estimatedDeliveryFulfillmentTz: timezone,
              estimatedDeliveryHolidays: Object.values(Pegasus.USHoliday),
            })
          }
        >
          Add Delivery Estimate
        </Action>
      </div>
    </div>
  );
}
